/**
 * Visible rendering of a HowTo step list.
 *
 * Pair this with `HowToStructuredData` driven by the *same* `HowToStep[]`, so
 * the steps a crawler reads from JSON-LD are always the steps a visitor sees.
 * Structured data that describes steps absent from the page is a rich-result
 * violation, and keeping one array as the source of truth makes that drift
 * impossible.
 */

import React from 'react';
import type { HowToStep } from '@/components/seo';

export interface HowToStepsProps {
  /** Section heading rendered above the steps. */
  title: string;
  /** Optional lead-in paragraph shown under the heading. */
  description?: string;
  /** Steps to render, in order. */
  steps: HowToStep[];
}

export function HowToSteps({ title, description, steps }: HowToStepsProps) {
  return (
    <section className="mb-16">
      <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">{title}</h2>
      {description && (
        <p className="text-lg text-muted-foreground max-w-3xl mx-auto text-center mb-10">
          {description}
        </p>
      )}
      <ol className="max-w-3xl mx-auto space-y-4">
        {steps.map((step) => (
          <li
            key={step.position}
            className="bg-card/50 backdrop-blur-sm rounded-xl border border-border/50 p-6 flex gap-5"
          >
            <span
              aria-hidden="true"
              className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-lg font-bold text-primary"
            >
              {step.position}
            </span>
            <div>
              <h3 className="text-xl font-bold mb-2">{step.name}</h3>
              <p className="text-muted-foreground leading-relaxed">{step.text}</p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}
